import {
  AREA_NAMES_PL,
  itemName,
  pickLine,
  parseHm,
  localMinutes,
  type ChipAction,
  type ClientAction,
  type ClientMessage,
  type GameEvent,
  type GameState,
  type Presence,
  type PromptPlan,
  type ReplyPayload,
  type ServerMessage,
} from "@squire/core";
import { SCENE_H, SCENE_W } from "@squire/sprites";
import { SceneView } from "../render/sceneView.js";
import { LocalTransport } from "../net/local.js";
import { WsTransport } from "../net/ws.js";
import type { ConnectionStatus, Transport } from "../net/transport.js";
import { CameraPresence } from "../presence/camera.js";
import {
  createVoice,
  isNative,
  noInput,
  silentOutput,
  type VoiceInput,
  type VoiceOutput,
} from "../voice/index.js";
import { checkUpdate, describeBuild, downloadUpdate } from "./update.js";
import { parseDebug } from "./debug.js";
import { openStart } from "../ui/start.js";
import { openCreator } from "../ui/creator.js";
import { DialogView, type DialogChip } from "../ui/dialog.js";
import { h } from "../ui/dom.js";
import { Hud, fridgeNote, journalView, overlay, shopView } from "../ui/panels.js";
import { outboundView, type LlmCall } from "../ui/outbound.js";
import { settingsView } from "../ui/settings.js";
import { ConversationController } from "./conversation.js";
import { saveDevice, type DeviceSettings } from "./device.js";
import { PromptRunner } from "./promptRunner.js";
import { Awake } from "../system/awake.js";
import { Watchdog } from "../system/watchdog.js";
import { SoftRestart } from "../system/softRestart.js";
import { isNight } from "@squire/core";

const REPLY_TIMEOUT = 35_000;
const MAX_PENDING = 8;
/** Second device waits a moment, so two screens side by side look like walking through the door. */
const ARRIVE_DELAY = 1500;

export interface AppOptions {
  clock?: () => number;
  /** Soak tests: skip voice and camera. */
  headless?: boolean;
}

/** Wires transport, scene, conversation, scheduler, presence and UI together. */
export class App {
  state: GameState | null = null;
  private readonly root: HTMLElement;
  private readonly stage: HTMLDivElement;
  private scene: SceneView | null = null;
  private dialog!: DialogView;
  private hud!: Hud;
  private transport!: Transport;
  private conv!: ConversationController;
  private runner!: PromptRunner;
  private camera: CameraPresence | null = null;
  private input: VoiceInput = noInput;
  private output: VoiceOutput = silentOutput;
  private readonly pending = new Map<
    string,
    { resolve: (r: ReplyPayload | null) => void; timer: ReturnType<typeof setTimeout> }
  >();
  private seq = 0;
  private lastTouchAt = 0;
  private creatorOpen = false;
  private overlayClose: (() => void) | null = null;
  private away = false;
  private arriveTimer: ReturnType<typeof setTimeout> | null = null;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private toast!: HTMLDivElement;
  private status!: HTMLDivElement;
  private callHere!: HTMLButtonElement;
  private cameraTimer: ReturnType<typeof setInterval> | null = null;
  private readonly clock: () => number;
  readonly awake = new Awake();
  watchdog: Watchdog | null = null;
  private softRestart: SoftRestart | null = null;
  private nightTimer: ReturnType<typeof setInterval> | null = null;
  private dimmed = false;
  /** Bounded log of what the companion did on its own (soak tests, debugging). */
  readonly log: { at: number; kind: string; type: string | null }[] = [];
  stalls = 0;

  constructor(
    root: HTMLElement,
    readonly device: DeviceSettings,
    private readonly opts: AppOptions = {},
  ) {
    this.root = root;
    this.clock = opts.clock ?? (() => Date.now());
    this.stage = h("div", { class: "stage" });
    root.append(this.stage);
  }

  async start(): Promise<void> {
    if (!this.opts.headless) {
      const d = this.device;
      const v = await createVoice(d.cloudStt && !d.local ? () => d : undefined);
      this.input = v.input;
      this.output = v.output;
      this.output.setVoice(this.device.voiceName);
    }
    this.toast = h("div", { class: "toast hidden", role: "status" });
    this.status = h("div", { class: "offline hidden" }, "offline");
    this.callHere = h(
      "button",
      {
        class: "primary hidden",
        style: {
          position: "absolute",
          top: "40%",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: "6",
          fontSize: "1.6rem",
        },
        onclick: () => this.send({ t: "call_here" }),
      },
      "Zawołaj tutaj",
    );
    this.root.append(this.toast, this.status, this.callHere);
    this.hud = new Hud(this.root);
    this.root.append(
      h(
        "div",
        { class: "topright" },
        h("button", { onclick: () => this.openJournal() }, "Dziennik"),
        h("button", { "aria-label": "Lista zakupów", onclick: () => this.openFridge() }, "Lodówka"),
        h("button", { "aria-label": "Ustawienia", onclick: () => this.openSettings() }, "⚙"),
      ),
    );
    this.dialog = new DialogView(this.root, "Giermek", {
      onSubmit: (t) => {
        this.runner.resolved();
        void this.conv.userSays(t);
      },
      onMic: () => {
        this.runner.resolved();
        void this.conv.tapToTalk();
      },
      onTapLine: () => {
        this.output.stop();
        this.dialog.flush();
      },
    });
    this.dialog.say("Chwileczkę, rozpakowuję się…");

    this.conv = new ConversationController({
      input: this.input,
      output: this.output,
      voiceOn: () => this.device.voiceOn,
      send: (text) => this.say(text),
      state: () => this.state,
      now: this.clock,
      view: this.dialog,
      setMode: (m) => this.scene?.setConversation(m),
      onActivity: () => this.touch(),
      onVoiceError: (m) => this.showToast(m),
      command: (text) => this.debugCommand(text),
    });

    this.runner = new PromptRunner({
      state: () => this.state,
      deviceId: () => this.device.id,
      profile: () => this.device.profile,
      presence: () => this.presence(),
      busy: () => this.conv.busy || this.creatorOpen || this.overlayClose !== null,
      now: this.clock,
      present: (p) => this.presentPrompt(p),
      act: (a) => this.act(a),
    });

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Warsaw";
    this.transport = this.device.local
      ? new LocalTransport(
          { id: this.device.id, name: this.device.name, profile: this.device.profile },
          tz,
          this.clock,
        )
      : new WsTransport(this.device, () => !document.hidden, tz);
    this.transport.onMessage = (m) => this.onMessage(m);
    this.transport.onStatus = (s) => this.onStatus(s);
    this.transport.start();

    document.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("pointerdown", this.onPointer, { passive: true });
    this.runner.start();
    if (!this.opts.headless) {
      this.cameraTimer = setInterval(() => void this.updateCamera(), 60_000);
      void this.awake.enable();
      // Once per start (the app reloads itself daily at 04:00), only in the Android app.
      if (isNative())
        setTimeout(() => {
          void checkUpdate(this.device).then((b) => {
            if (b) this.showToast(`Jest nowa wersja (${describeBuild(b)}). Ustawienia → Aktualizuj.`);
          });
        }, 15_000);
    }
  }

  // ---------- networking ----------

  private send(msg: ClientMessage): void {
    this.transport.send(msg);
  }

  act(action: ClientAction): void {
    this.send({ t: "act", action });
  }

  private say(text: string): Promise<ReplyPayload | null> {
    const id = `s${++this.seq}`;
    if (this.pending.size >= MAX_PENDING) {
      const [oldest, p] = this.pending.entries().next().value!;
      clearTimeout(p.timer);
      p.resolve(null);
      this.pending.delete(oldest);
    }
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        resolve(null);
      }, REPLY_TIMEOUT);
      this.pending.set(id, { resolve, timer });
      this.send({ t: "say", id, text });
    });
  }

  private onStatus(s: ConnectionStatus) {
    this.status.classList.toggle("hidden", s === "online");
    this.status.textContent = s === "connecting" ? "łączę…" : "offline";
  }

  private onMessage(m: ServerMessage) {
    switch (m.t) {
      case "state":
        this.onState(m.state);
        break;
      case "events":
        this.onEvents(m.events);
        break;
      case "reply": {
        const p = this.pending.get(m.re);
        if (p) {
          clearTimeout(p.timer);
          this.pending.delete(m.re);
          p.resolve(m.reply);
        }
        break;
      }
      case "error":
        this.showToast(m.message);
        break;
      default:
        break;
    }
  }

  private onState(s: GameState) {
    const first = !this.state;
    this.state = s;
    if (!this.scene) {
      this.scene = new SceneView(this.stage, s, {
        clock: this.clock,
        nightDim: this.device.profile === "wall" && this.device.nightDim,
      });
      this.scene.canvas.addEventListener("click", this.onCanvasClick);
      this.scene.start();
      this.watchdog = new Watchdog(
        () => [this.scene?.lastFrameAt ?? 0, this.runner.lastTickAt],
        this.clock,
        this.opts.headless
          ? () => {
              this.stalls++;
              console.error("watchdog: render loop or scheduler stalled");
            }
          : undefined,
      );
      this.watchdog.start();
      this.softRestart = new SoftRestart(
        () => this.state?.settings.timezone ?? "Europe/Warsaw",
        () => this.conv.busy || this.creatorOpen || this.overlayClose !== null,
        this.clock,
      );
      this.softRestart.start();
      this.nightTimer = setInterval(() => this.updateNight(), 60_000);
    }
    this.scene.setState(s);
    this.hud.update(s, this.clock());
    this.dialog.setName(s.settings.companionName);
    const away = s.location.deviceId !== this.device.id;
    this.setAway(away, first);
    if (first) {
      this.dialog.say(
        away
          ? `Jestem teraz na: ${s.location.deviceName || "innym urządzeniu"}.`
          : `Cześć${s.settings.userName ? `, ${s.settings.userName}` : ""}! Stuknij we mnie, żeby pogadać.`,
      );
      void this.updateCamera();
    }
    if (!s.onboarded && !this.creatorOpen) void this.onboard();
  }

  private setAway(away: boolean, immediate: boolean) {
    if (away === this.away && !immediate) return;
    if (this.arriveTimer) clearTimeout(this.arriveTimer);
    this.arriveTimer = null;
    this.callHere.classList.toggle("hidden", !away);
    this.callHere.textContent =
      away && this.state?.location.deviceName
        ? `Zawołaj tutaj (jest na: ${this.state.location.deviceName})`
        : "Zawołaj tutaj";
    if (away) {
      this.conv?.interrupt();
      this.away = true;
      this.scene?.setAway(true);
      return;
    }
    const apply = () => {
      this.away = false;
      this.scene?.setAway(false);
      if (!immediate) this.dialog.say("Już jestem! Co słychać?");
    };
    if (immediate) apply();
    else this.arriveTimer = setTimeout(apply, ARRIVE_DELAY);
  }

  private onEvents(events: GameEvent[]) {
    if (!this.away) this.scene?.handleEvents(events);
    this.celebrateLine(events);
    for (const e of events) {
      if (e.type === "xp")
        this.showToast(
          e.area === "home"
            ? `+${e.gold} złota · ${AREA_NAMES_PL.home}`
            : `+${e.xp} XP ${AREA_NAMES_PL[e.area]}${e.bonus ? " (odpoczynek ×2)" : ""}`,
        );
      else if (e.type === "level_up") this.showToast(`${AREA_NAMES_PL[e.area]}: poziom ${e.level}!`);
      else if (e.type === "item") this.showToast(`Nowy przedmiot: ${itemName(e.itemId)}`);
      else if (e.type === "body_stage") this.showToast("Silniejszy! Nowe stadium ciała.");
      else if (e.type === "gold") this.showToast(`+${e.amount} złota`);
    }
  }

  /** SPEC 4.1: a stage-up (and a new piece of furniture) is a moment, with a short line. */
  private celebrateLine(events: GameEvent[]) {
    const s = this.state;
    if (!s || this.away || this.conv.busy || (s.mute && this.clock() < s.mute.until)) return;
    const v = s.nudge.variant;
    let line: string | null = null;
    const stage = events.find((e) => e.type === "body_stage");
    const item = events.find((e) => e.type === "item");
    if (stage) line = pickLine("stageUp", v, {});
    else if (item && item.type === "item") line = pickLine("newItem", v, { item: itemName(item.itemId) });
    if (line) {
      const text = line;
      // Let the reply finish first.
      setTimeout(() => !this.conv.busy && void this.conv.speak(text, { listen: false }), 2500);
    }
  }

  private showToast(text: string) {
    this.toast.textContent = text;
    this.toast.classList.remove("hidden");
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toast.classList.add("hidden"), 3500);
  }

  // ---------- prompts ----------

  private presentPrompt(plan: PromptPlan) {
    this.log.push({ at: this.clock(), kind: plan.kind, type: plan.nudgeType });
    if (this.log.length > 300) this.log.splice(0, this.log.length - 300);
    this.scene?.wake(120_000);
    this.dialog.setChips(plan.chips.map((c) => this.chip(c.label, c.action)));
    void this.conv.speak(plan.text, { listen: plan.listen, voice: plan.speak });
  }

  private chip(label: string, action: ChipAction): DialogChip {
    return {
      label,
      onPick: () => {
        this.runner.resolved();
        this.touch();
        this.conv.interrupt();
        let line = "";
        switch (action.type) {
          case "complete_quest":
            this.act({ type: "complete_quest", id: action.id });
            this.act({ type: "prompt_outcome", outcome: "answered" });
            line = "Jest! Dobra robota.";
            break;
          case "postpone_quest":
            this.act({ type: "postpone_quest", id: action.id });
            this.act({ type: "prompt_outcome", outcome: "answered" });
            line = "Przesunięte na jutro, bez spiny.";
            break;
          case "reminder_ack":
            this.act({ type: "reminder_ack", id: action.id, outcome: action.outcome });
            line =
              action.outcome === "done"
                ? "Super."
                : action.outcome === "hour"
                  ? "Dobra, przypomnę za godzinę."
                  : "Jasne, jutro.";
            break;
          case "rate_week":
            this.act({ type: "rate_week", value: action.value });
            line =
              action.value >= 4
                ? "Też to widzę. Dobra robota w tym tygodniu."
                : "Dzięki, że mówisz wprost. Zaczniemy od małego kroku.";
            break;
          case "log_challenge":
            this.act({
              type: "log_activity",
              area: action.area,
              text: action.text,
              size: "S",
              animation: "train",
            });
            this.act({ type: "prompt_outcome", outcome: "answered" });
            line = "Mocne!";
            break;
          case "not_yet":
            this.act({ type: "prompt_outcome", outcome: "answered" });
            line = "Spoko, zajrzę później.";
            break;
          case "later":
            this.act({ type: "prompt_outcome", outcome: "dismissed" });
            line = "Jasne, jestem tu.";
            break;
        }
        void this.conv.speak(line, { listen: false });
      },
    };
  }

  // ---------- presence and input ----------

  private presence(): Presence {
    return {
      cameraOn: Boolean(this.camera?.running),
      lastMotionAt: this.camera?.lastMotionAt ?? 0,
      lastTouchAt: this.lastTouchAt,
    };
  }

  private touch() {
    this.lastTouchAt = this.clock();
    this.scene?.wake();
    if (this.dimmed) this.updateNight();
  }

  /** Wall profile at night: minimum screen brightness on Android until touched or motion. */
  private updateNight() {
    const s = this.state;
    if (!s || this.device.profile !== "wall" || !this.device.nightDim) return;
    const recentlyActive = this.clock() - Math.max(this.lastTouchAt, this.camera?.lastMotionAt ?? 0) < 60_000;
    const dim = isNight(s, this.clock()) && !recentlyActive;
    if (dim !== this.dimmed) {
      this.dimmed = dim;
      void this.awake.setDim(dim);
    }
  }

  private onPointer = () => this.touch();

  private onVisibility = () => {
    this.send({ t: "visible", visible: !document.hidden });
    if (document.hidden) this.conv.interrupt();
  };

  private onCanvasClick = (e: MouseEvent) => {
    const c = this.scene!.canvas;
    const r = c.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * SCENE_W;
    const y = ((e.clientY - r.top) / r.height) * SCENE_H;
    if (x >= 164 && x <= 190 && y >= 40 && y <= 94) {
      this.openFridge();
      return;
    }
    const d = this.scene!.director;
    if (!this.away && Math.abs(x - d.x) < 14 && y > d.y - 40 && y < d.y + 4) {
      this.runner.resolved();
      void this.conv.tapToTalk();
    }
  };

  /** Camera runs only on wall devices, during active hours, when enabled. */
  private async updateCamera() {
    const s = this.state;
    const want =
      !this.opts.headless &&
      this.device.cameraOn &&
      this.device.profile === "wall" &&
      !document.hidden &&
      s !== null &&
      (() => {
        const m = localMinutes(this.clock(), s.settings.timezone);
        const from = parseHm(s.settings.briefFrom) ?? 420;
        const until = parseHm(s.settings.nightFrom) ?? 1380;
        return m >= from && m < until;
      })();
    if (want && !this.camera) this.camera = new CameraPresence(() => this.scene?.wake());
    if (want && this.camera && !this.camera.running) {
      const ok = await this.camera.start();
      if (!ok) this.showToast("Brak dostępu do kamery, zaczepki tylko po dotknięciu ekranu.");
    }
    if (!want && this.camera?.running) this.camera.stop();
  }

  // ---------- overlays ----------

  private open(title: string, body: Node) {
    this.overlayClose?.();
    this.conv.interrupt();
    this.overlayClose = overlay(this.root, title, body, () => (this.overlayClose = null));
  }

  private closeOverlay() {
    this.overlayClose?.();
    this.overlayClose = null;
  }

  openJournal(): void {
    if (this.state) this.open("Dziennik", journalView(this.state, this.clock()));
  }

  openFridge(): void {
    if (!this.state) return;
    const render = () => {
      const s = this.state!;
      return fridgeNote(
        s,
        (id) => {
          this.act({ type: "shopping_toggle", id });
          setTimeout(() => this.state && this.openFridge(), 300);
        },
        () => {
          this.act({ type: "shopping_remove", items: s.shopping.filter((i) => i.done).map((i) => i.text) });
          setTimeout(() => this.state && this.openFridge(), 300);
        },
      );
    };
    this.open("Lodówka", render());
  }

  private debugCommand(text: string): string | null {
    if (!this.device.debug) return null;
    const c = parseDebug(text);
    if (!c) return null;
    if (c.kind === "anim") this.scene?.debugAnim(c.id, c.ms);
    else if (c.kind === "go") this.scene?.debugGo(c.place, c.ms, c.friend);
    else if (c.kind === "act") this.act(c.action);
    else if (c.kind === "reset") void this.resetProfile();
    return c.text;
  }

  private async resetProfile() {
    if (this.transport instanceof LocalTransport) {
      for (const o of await this.transport.room.resetProfile()) this.onMessage(o.msg);
      return;
    }
    const res = await fetch(`${this.device.serverUrl.replace(/\/$/, "")}/api/reset`, {
      method: "POST",
      headers: { authorization: `Bearer ${this.device.token}`, "content-type": "application/json" },
      body: JSON.stringify({ confirm: "RESET" }),
    }).catch(() => null);
    if (!res?.ok) this.showToast("Nie udało się zresetować.");
  }

  openOutbound(): void {
    const d = this.device;
    const loadLlm = async (): Promise<LlmCall[] | null> => {
      if (d.local || !d.serverUrl) return null;
      try {
        const res = await fetch(`${d.serverUrl.replace(/\/$/, "")}/api/llm-log`, {
          headers: { authorization: `Bearer ${d.token}` },
          signal: AbortSignal.timeout(10_000),
        });
        return res.ok ? ((await res.json()) as LlmCall[]) : null;
      } catch {
        return null;
      }
    };
    this.open("Co wysłałem", outboundView(loadLlm));
  }

  openShop(): void {
    if (this.state)
      this.open(
        "Sklep",
        shopView(this.state, (id) => (this.act({ type: "buy_item", itemId: id }), this.closeOverlay())),
      );
  }

  openSettings(): void {
    if (!this.state) return;
    this.open(
      "Ustawienia",
      settingsView(this.state, this.device, {
        saveGame: (patch) => this.act({ type: "update_settings", patch }),
        saveDevice: (d) => {
          const reconnect =
            d.serverUrl !== this.device.serverUrl ||
            d.token !== this.device.token ||
            d.profile !== this.device.profile ||
            d.name !== this.device.name ||
            d.cloudStt !== this.device.cloudStt;
          Object.assign(this.device, d);
          saveDevice(this.device);
          this.output.setVoice(d.voiceName);
          this.closeOverlay();
          void this.updateCamera();
          if (reconnect) location.reload();
        },
        editLook: () => {
          this.closeOverlay();
          void this.editLook();
        },
        exportProfile: () => void this.exportProfile(),
        importProfile: (f) => void this.importProfile(f),
        openShop: () => this.openShop(),
        voices: () => this.output.voices(),
        testVoice: () => void this.output.speak("Cześć, tu twój giermek. Tak brzmię."),
        checkUpdate: () => checkUpdate(this.device),
        openOutbound: () => this.openOutbound(),
        downloadUpdate: () => downloadUpdate(this.device),
        testMic: async () =>
          this.input.diagnose
            ? this.input.diagnose()
            : this.input.kind === "none"
              ? "Ta przeglądarka nie ma rozpoznawania mowy."
              : "Rozpoznawanie mowy przeglądarki.",
      }),
    );
  }

  private async onboard() {
    this.creatorOpen = true;
    const s = this.state!;
    const r = await openCreator(this.root, {
      look: s.look,
      companionName: s.settings.companionName,
      userName: s.settings.userName,
    });
    const start = r ? await openStart(this.root) : {};
    this.creatorOpen = false;
    if (r) {
      this.act({
        type: "onboard",
        look: r.look,
        companionName: r.companionName,
        userName: r.userName,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        start,
      });
      void this.conv.speak(
        `Cześć${r.userName ? `, ${r.userName}` : ""}! Jestem ${r.companionName}. ${Object.values(start).some((l) => l > 1) ? "Widzę, że sporo już masz na koncie. Od tego zaczynamy." : "Mieszkanie na razie puste, ale to się zmieni."} Co dziś robimy?`,
        { listen: true },
      );
    }
  }

  private async editLook() {
    const s = this.state;
    if (!s) return;
    this.creatorOpen = true;
    const r = await openCreator(
      this.root,
      { look: s.look, companionName: s.settings.companionName, userName: s.settings.userName },
      { title: "Wygląd", cancellable: true },
    );
    this.creatorOpen = false;
    if (r) {
      this.act({ type: "set_look", look: r.look });
      this.act({ type: "update_settings", patch: { companionName: r.companionName, userName: r.userName } });
    }
  }

  private async exportProfile() {
    let blob: Blob;
    if (this.transport instanceof LocalTransport) {
      blob = new Blob([JSON.stringify(await this.transport.room.exportProfile(), null, 1)], {
        type: "application/json",
      });
    } else {
      const res = await fetch(`${this.device.serverUrl.replace(/\/$/, "")}/api/export`, {
        headers: { authorization: `Bearer ${this.device.token}` },
      });
      if (!res.ok) return this.showToast("Eksport się nie udał.");
      blob = await res.blob();
    }
    const a = h("a", {
      href: URL.createObjectURL(blob),
      download: `squire-${new Date().toISOString().slice(0, 10)}.json`,
    });
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  }

  private async importProfile(file: File) {
    let data: unknown;
    try {
      data = JSON.parse(await file.text());
    } catch {
      return this.showToast("To nie jest plik profilu.");
    }
    if (this.transport instanceof LocalTransport) {
      try {
        const out = await this.transport.room.importProfile(data);
        for (const o of out) this.onMessage(o.msg);
      } catch {
        return this.showToast("Nie udało się zaimportować profilu.");
      }
    } else {
      const res = await fetch(`${this.device.serverUrl.replace(/\/$/, "")}/api/import`, {
        method: "POST",
        headers: { authorization: `Bearer ${this.device.token}`, "content-type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) return this.showToast("Nie udało się zaimportować profilu.");
    }
    this.closeOverlay();
    this.showToast("Profil zaimportowany.");
  }

  /** Debug and soak hooks. */
  stats(): Record<string, number> {
    return {
      frames: this.scene?.loop.frames ?? 0,
      lastFrameAt: this.scene?.lastFrameAt ?? 0,
      lastTickAt: this.runner.lastTickAt,
      pending: this.pending.size,
      domNodes: document.getElementsByTagName("*").length,
      cachedFrames: this.scene?.cacheSize() ?? 0,
      stalls: this.stalls,
      logSize: this.log.length,
    };
  }
}
